import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Euro,
  Loader2,
  Search,
  Building2,
  Link as LinkIcon,
  Image,
  Mic,
  ExternalLink,
  Calculator,
  Send,
  Eye,
  MoreHorizontal
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  estimated: { label: 'Estimation envoyée', color: 'bg-blue-100 text-blue-700', icon: Euro },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
  converted: { label: 'Projet créé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const ProjectRequestsManagementPage = () => {
  const { token } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [estimatedHours, setEstimatedHours] = useState('');
  const [estimationNotes, setEstimationNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get(`/project-requests/${params}`);
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEstimation = async () => {
    if (!selectedRequest || !estimatedHours) {
      toast.error('Veuillez entrer une estimation');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.put(`/project-requests/${selectedRequest.id}/estimate`, {
        estimated_hours: parseFloat(estimatedHours),
        estimation_notes: estimationNotes || null
      });
      
      toast.success('Estimation envoyée au client !');
      setShowEstimateModal(false);
      setSelectedRequest(null);
      setEstimatedHours('');
      setEstimationNotes('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setActionLoading(false);
    }
  };

  const openEstimateModal = (request) => {
    setSelectedRequest(request);
    setEstimatedHours(request.estimated_hours?.toString() || '');
    setEstimationNotes(request.estimation_notes || '');
    setShowEstimateModal(true);
  };

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const filteredRequests = requests.filter(r => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      r.title?.toLowerCase().includes(search) ||
      r.client_name?.toLowerCase().includes(search) ||
      r.company_name?.toLowerCase().includes(search)
    );
  });

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    estimated: requests.filter(r => r.status === 'estimated').length,
    converted: requests.filter(r => r.status === 'converted').length,
    total: requests.length
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-24">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Demandes de projet
            </h1>
            <p className="text-slate-500 mt-1">
              Étudiez les demandes clients et envoyez vos estimations
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
                    <p className="text-xs text-slate-500">À estimer</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.estimated}</p>
                    <p className="text-xs text-slate-500">Attente client</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.converted}</p>
                    <p className="text-xs text-slate-500">Convertis</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">À estimer</SelectItem>
                <SelectItem value="estimated">Attente client</SelectItem>
                <SelectItem value="converted">Convertis</SelectItem>
                <SelectItem value="rejected">Refusés</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Requests Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <Card className="card">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Aucune demande de projet</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold text-slate-700">Demande</TableHead>
                      <TableHead className="font-semibold text-slate-700">Client</TableHead>
                      <TableHead className="font-semibold text-slate-700">Statut</TableHead>
                      <TableHead className="font-semibold text-slate-700">Contenu</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Estimation</TableHead>
                      <TableHead className="font-semibold text-slate-700">Date</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((request) => {
                      const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <TableRow 
                          key={request.id}
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => openDetailModal(request)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shrink-0">
                                <FileText className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-slate-800 truncate max-w-[200px]">
                                  {request.title}
                                </p>
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                  {request.description?.substring(0, 50) || 'Aucune description'}...
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="text-slate-700">{request.client_name || '-'}</p>
                              <p className="text-xs text-slate-400">{request.company_name || ''}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {request.audio_url && (
                                <Badge variant="outline" className="text-xs">
                                  <Mic className="w-3 h-3 mr-1" />
                                  Audio
                                </Badge>
                              )}
                              {request.inspiration_links?.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  <LinkIcon className="w-3 h-3 mr-1" />
                                  {request.inspiration_links.length}
                                </Badge>
                              )}
                              {request.images?.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  <Image className="w-3 h-3 mr-1" />
                                  {request.images.length}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {request.estimated_hours ? (
                              <div>
                                <p className="font-bold text-lg" style={{ color: theme.colors?.primary }}>
                                  {request.estimated_hours}h
                                </p>
                                <p className="text-xs text-slate-500">
                                  {(request.estimated_hours * (request.hourly_rate || 75)).toLocaleString('fr-FR')}€
                                </p>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-500 whitespace-nowrap">
                              {new Date(request.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openDetailModal(request)}
                              >
                                <Eye className="w-4 h-4 text-slate-500" />
                              </Button>
                              {(request.status === 'pending' || request.status === 'estimated') && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => openEstimateModal(request)}
                                >
                                  <Calculator className="w-4 h-4" style={{ color: theme.colors?.accent }} />
                                </Button>
                              )}
                              {request.status === 'converted' && request.project_id && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => navigate(`/projects/${request.project_id}`)}
                                >
                                  <ExternalLink className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </main>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              {selectedRequest?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Client */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="font-medium">{selectedRequest.client_name}</p>
                  <p className="text-sm text-slate-500">{selectedRequest.company_name || 'Particulier'}</p>
                </div>
              </div>
              
              {/* Description */}
              {selectedRequest.description && (
                <div className="space-y-2">
                  <Label>Description</Label>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedRequest.description}</p>
                </div>
              )}
              
              {/* Audio Transcript */}
              {selectedRequest.audio_transcript && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-purple-600" />
                    Transcription audio
                  </Label>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-slate-700 whitespace-pre-wrap">{selectedRequest.audio_transcript}</p>
                  </div>
                </div>
              )}
              
              {/* Inspiration Links */}
              {selectedRequest.inspiration_links?.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4 text-emerald-600" />
                    Liens d'inspiration
                  </Label>
                  <div className="space-y-2">
                    {selectedRequest.inspiration_links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-cyan-600 hover:underline text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {link}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Images */}
              {selectedRequest.images?.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-orange-600" />
                    Images de référence
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedRequest.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Reference ${idx + 1}`}
                        className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80"
                        onClick={() => window.open(img, '_blank')}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Preferences */}
              {(selectedRequest.budget_preference || selectedRequest.deadline_preference) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedRequest.budget_preference && (
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Budget estimé</Label>
                      <p className="font-medium">
                        {selectedRequest.budget_preference === 'low' && 'Moins de 1 000€'}
                        {selectedRequest.budget_preference === 'medium' && '1 000€ - 5 000€'}
                        {selectedRequest.budget_preference === 'high' && '5 000€ - 10 000€'}
                        {selectedRequest.budget_preference === 'very_high' && 'Plus de 10 000€'}
                        {selectedRequest.budget_preference === 'unknown' && 'À définir'}
                      </p>
                    </div>
                  )}
                  {selectedRequest.deadline_preference && (
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Délai souhaité</Label>
                      <p className="font-medium">
                        {selectedRequest.deadline_preference === 'urgent' && 'Urgent (< 1 semaine)'}
                        {selectedRequest.deadline_preference === 'short' && 'Court (1-2 semaines)'}
                        {selectedRequest.deadline_preference === 'medium' && 'Normal (2-4 semaines)'}
                        {selectedRequest.deadline_preference === 'flexible' && 'Flexible (1 mois+)'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
            {selectedRequest?.status === 'pending' && (
              <Button 
                onClick={() => {
                  setShowDetailModal(false);
                  openEstimateModal(selectedRequest);
                }}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Calculator className="w-4 h-4 mr-1" />
                Envoyer estimation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estimate Modal */}
      <Dialog open={showEstimateModal} onOpenChange={setShowEstimateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              Estimation du projet
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-800">{selectedRequest.title}</p>
                <p className="text-sm text-slate-500">{selectedRequest.client_name}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Heures estimées *</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    step="0.5"
                    value={estimatedHours}
                    onChange={(e) => setEstimatedHours(e.target.value)}
                    placeholder="Ex: 10"
                    className="w-32"
                  />
                  <span className="text-slate-500">heures</span>
                </div>
              </div>
              
              {estimatedHours && (
                <div className="p-4 rounded-xl" style={{ backgroundColor: `${theme.colors?.accent}15` }}>
                  <p className="text-sm text-slate-500">Total client</p>
                  <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                    {(parseFloat(estimatedHours) * (selectedRequest.hourly_rate || 75)).toLocaleString('fr-FR')}€ HT
                  </p>
                  <p className="text-xs text-slate-400">
                    {estimatedHours}h × {selectedRequest.hourly_rate || 75}€/h
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Notes pour le client</Label>
                <Textarea
                  value={estimationNotes}
                  onChange={(e) => setEstimationNotes(e.target.value)}
                  placeholder="Détails de l'estimation, livrables inclus..."
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEstimateModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmitEstimation}
              disabled={actionLoading || !estimatedHours}
              className="gap-1"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Envoyer l'estimation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectRequestsManagementPage;
